import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  IconButton,
  Box,
  Typography,
} from '@mui/material';
import {
  Close as CloseIcon,
  LocationOn as LocationIcon,
} from '@mui/icons-material';
import { GooglePlacesAutocomplete } from './GooglePlacesAutocomplete';
import { useDataProvider, useNotify } from 'react-admin';

interface Location {
  id?: string;
  name: string;
  streetLine1: string;
  streetLine2?: string;
  city: string;
  province: string;
  postalCode: string;
  country: string;
  latitude?: number;
  longitude?: number;
}

interface LocationDialogProps {
  open: boolean;
  onClose: () => void;
  onLocationCreated: (location: Location) => void;
  initialValues?: Partial<Location>;
  title?: string;
}

export const LocationDialog: React.FC<LocationDialogProps> = ({
  open,
  onClose,
  onLocationCreated,
  initialValues = {},
  title = 'Add Location',
}) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const [formData, setFormData] = useState<Location>({
    name: '',
    streetLine1: '',
    streetLine2: '',
    city: '',
    province: '',
    postalCode: '',
    country: 'Canada',
    ...initialValues,
  });
  const [saving, setSaving] = useState(false);

  const handleChange = (field: keyof Location) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleAddressSelect = useCallback((address: {
    street: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
    latitude?: number;
    longitude?: number;
  }) => {
    setFormData(prev => ({
      ...prev,
      name: prev.name || address.street, // Set name to street address if not already filled in
      streetLine1: address.street,
      city: address.city,
      province: address.state,
      postalCode: address.zipCode,
      country: address.country,
      latitude: address.latitude,
      longitude: address.longitude,
    }));
  }, []);

  const handleSave = async () => {
    if (!formData.name || !formData.streetLine1 || !formData.city || !formData.province) {
      notify('Please fill in all required fields', { type: 'warning' });
      return;
    }

    setSaving(true);
    try {
      let response;
      if (formData.id) {
        // Update existing location
        response = await dataProvider.update('crm/locations', {
          id: formData.id,
          data: formData,
          previousData: initialValues,
        });
        notify('Location updated successfully', { type: 'success' });
      } else {
        // Create new location
        response = await dataProvider.create('crm/locations', {
          data: formData,
        });
        notify('Location created successfully', { type: 'success' });
      }
      
      onLocationCreated(response.data);
      handleClose();
    } catch (error) {
      console.error('Error saving location:', error);
      notify(`Error ${formData.id ? 'updating' : 'creating'} location`, { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      streetLine1: '',
      streetLine2: '',
      city: '',
      province: '',
      postalCode: '',
      country: 'Canada',
      ...initialValues,
    });
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LocationIcon color="primary" />
            {title}
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Location Name"
              value={formData.name}
              onChange={handleChange('name')}
              placeholder="e.g., Main Office, Client Site, etc."
              required
            />
          </Grid>
          
          <Grid item xs={12}>
            <GooglePlacesAutocomplete
              value={formData.streetLine1}
              onChange={value => setFormData(prev => ({ ...prev, streetLine1: value }))}
              onAddressSelect={handleAddressSelect}
              label="Street Address"
              placeholder="Start typing an address..."
              required
            />
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Address Line 2"
              value={formData.streetLine2}
              onChange={handleChange('streetLine2')}
              placeholder="Apartment, suite, unit, building, floor, etc."
            />
          </Grid>
          
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="City"
              value={formData.city}
              onChange={handleChange('city')}
              required
            />
          </Grid>
          
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Province/State"
              value={formData.province}
              onChange={handleChange('province')}
              required
            />
          </Grid>
          
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Postal Code"
              value={formData.postalCode}
              onChange={handleChange('postalCode')}
            />
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Country"
              value={formData.country}
              onChange={handleChange('country')}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={saving}>
          Cancel
        </Button>
        <Button 
          onClick={handleSave} 
          variant="contained" 
          disabled={saving}
        >
          {saving 
            ? (formData.id ? 'Updating...' : 'Creating...') 
            : (formData.id ? 'Update Location' : 'Create Location')
          }
        </Button>
      </DialogActions>
    </Dialog>
  );
};